/**
 * Storage utilities for the Advanced Retirement Calculator
 * Handles versioned localStorage persistence
 * @module domain/retirement/storage
 */

import { STORAGE_SCHEMA_VERSION } from "./constants";
import type { VersionedStoragePayload } from "./types";

/**
 * Build the storage key for retirement calculator data
 * Format: moneytor:retirecalc:advanced:v{version}:{userId}:{affiliateId}
 */
export function buildStorageKey(userId: string | number, affiliateId?: string | number | null): string {
  const affiliate = affiliateId ?? "na";
  return `moneytor:retirecalc:advanced:v${STORAGE_SCHEMA_VERSION}:${userId}:${affiliate}`;
}

/**
 * Create a versioned payload for storage
 */
export function createVersionedPayload<T>(data: T): VersionedStoragePayload<T> {
  return {
    version: STORAGE_SCHEMA_VERSION,
    timestamp: Date.now(),
    data,
  };
}

/**
 * Parse and validate a versioned payload from storage.
 * Only accepts the CURRENT version — old versions are discarded so
 * stale data with outdated risk-profile defaults is not loaded.
 * Returns null if invalid or unrecognised version.
 */
export function parseVersionedPayload<T>(json: string): VersionedStoragePayload<T> | null {
  try {
    const parsed = JSON.parse(json);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "version" in parsed &&
      "data" in parsed &&
      parsed.version === STORAGE_SCHEMA_VERSION
    ) {
      return parsed as VersionedStoragePayload<T>;
    }
    return null;
  } catch {
    return null;
  }
}
